import { ReactElement, use } from 'react';
import { Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import SelectField from 'shared/components/formFields/select-field';

import ReadDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-devices-response';
import ReadDomipDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-domip-devices-response';
import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import useWanderingBoundaryPassageRowValidation from 'features/wandering-boundary-settings/hooks/wandering-boundary-passage-row-validation-hook';
import WanderingBoundaryPassageRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-passage-row-provider-context';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import styles from './detection-loop-select.module.scss';

interface Props {
  fieldName: Path<WanderingBoundaryPassage>;
}

const DetectionLoopSelect = ({ fieldName }: Readonly<Props>): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { form, wanderingBoundaryPassage } = use(WanderingBoundaryPassageRowProviderContext);
  const { validationRules } = useWanderingBoundaryPassageRowValidation(wanderingBoundaryPassage);
  const {
    detailsState: { detectionLoopDevices },
  } = use(WanderingBoundarySettingsProviderContext);

  const noDevices = !detectionLoopDevices || detectionLoopDevices.length === 0;

  const selectMenuItem = (device: ReadDevicesResponse | ReadDomipDevicesResponse): ReactElement => (
    <MenuItem key={device.id} value={device.id} sx={{ borderRadius: '24px', alignItems: 'flex-start' }} className={styles.menuItem}>
      <Typography variant="subtitle1">{device.name}</Typography>
      <BreadCrumb showEllipsis={device.breadCrumbs.length > 2} allElementNames={device.breadCrumbs}>
        {device.breadCrumbs.map((name) => (
          <Typography sx={{ margin: '2px 0px' }} key={name} variant="caption">
            {name}
          </Typography>
        ))}
      </BreadCrumb>
    </MenuItem>
  );

  return (
    <Controller
      name={fieldName}
      control={form.control}
      rules={validationRules.detectionLoopDeviceId}
      render={({ field, fieldState: { error } }) => {
        if (noDevices) {
          return (
            <TextField
              label={t('wanderingBoundaryPassageFields.detectionLoopSelectLabel')}
              variant="outlined"
              value={t('wanderingBoundaryPassageFields.noDetectionLoopDevicesLabel')}
              disabled
              className={styles.noDevicesField}
              error={!!error}
              helperText={error?.message?.toString()}
              slotProps={{
                input: {
                  sx: { fontStyle: 'italic' },
                },
              }}
            />
          );
        }

        return (
          <SelectField
            {...field}
            error={!!error}
            helperText={error?.message?.toString()}
            variant="outlined"
            label={t('wanderingBoundaryPassageFields.detectionLoopSelectLabel')}
            value={field.value || ''}
            onChange={(event) => field.onChange(event.target.value || null)}
            className={styles.selectField}
          >
            {detectionLoopDevices.map(selectMenuItem)}
          </SelectField>
        );
      }}
    />
  );
};

export default DetectionLoopSelect;
